import React, { useState, useEffect, useRef } from 'react';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { 
  Target, // for shots
  Circle, // for free throws
  AlertTriangle, // for fouls
  RefreshCw, // for rebounds
  ArrowRight, // for assists
  Hand, // for steals/blocks
  XCircle, // for turnovers
  Activity // default
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

/**
 * PlayByPlayFeed - Live activity feed showing game events
 * Uses WebSocket for real-time updates
 */
export function PlayByPlayFeed({ gameId, homeTeamName, awayTeamName, homeTeamColor, awayTeamColor }) {
  const [plays, setPlays] = useState([]);
  const [loading, setLoading] = useState(true);
  const wsRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetchPlays();
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [gameId]);

  // Connect to WebSocket for real-time updates
  const connectWebSocket = () => {
    if (!WS_URL) return;
    
    try {
      const ws = new WebSocket(`${WS_URL}/api/engine/ws/game/${gameId}`);
      
      ws.onopen = () => {
        console.log('PlayByPlay WebSocket connected');
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Handle new stat events (backend sends type: "stat", action: "add")
          if (data.type === 'stat' && data.action === 'add' && data.stat) {
            setPlays(prev => [data.stat, ...prev]);
          }
          
          // Handle stat event undo (backend sends type: "stat", action: "undo")
          if (data.type === 'stat' && data.action === 'undo' && data.reverted_seq) {
            setPlays(prev => prev.filter(p => p.seq !== data.reverted_seq));
          }
          
          // Handle full game state refresh
          if (data.type === 'game_update' || data.type === 'GAME_STATE_UPDATED') {
            fetchPlays();
          }
        } catch (e) {
          console.error('WS parse error:', e);
        }
      };
      
      ws.onclose = () => {
        console.log('PlayByPlay WebSocket closed');
        // Reconnect after 3 seconds
        setTimeout(connectWebSocket, 3000);
      };
      
      ws.onerror = (err) => {
        console.error('PlayByPlay WebSocket error:', err);
      };
      
      wsRef.current = ws;
    } catch (err) {
      console.error('Failed to connect WebSocket:', err);
    }
  };

  const fetchPlays = async () => {
    try {
      const res = await fetch(`${API_URL}/api/engine/games/${gameId}/play-by-play?limit=50`);
      if (res.ok) {
        const data = await res.json();
        // Reverse to show most recent first
        setPlays(data.reverse());
      }
    } catch (err) {
      console.error('Error fetching plays:', err);
    } finally {
      setLoading(false);
    }
  };

  // Get icon for event type
  const getEventIcon = (eventType, eventSubtype) => {
    switch (eventType) {
      case 'SHOT':
        return <Target className="w-4 h-4" />;
      case 'FT':
        return <Circle className="w-4 h-4" />;
      case 'FOUL':
        return <AlertTriangle className="w-4 h-4" />;
      case 'REBOUND':
        return <RefreshCw className="w-4 h-4" />;
      case 'ASSIST':
        return <ArrowRight className="w-4 h-4" />;
      case 'STEAL':
      case 'BLOCK':
        return <Hand className="w-4 h-4" />;
      case 'TURNOVER':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  // Format event description
  const getEventDescription = (play) => {
    const isTeamPlay = !play.player_id || play.player_name === 'Team';
    const playerName = isTeamPlay ? null : (play.player_name || `#${play.player_number || '?'}`);
    const points = play.points;
    
    // Team-level scoring (Quick Score mode)
    if (isTeamPlay) {
      const teamLabel = play.team_side === 'home' ? (homeTeamName || 'Home') : (awayTeamName || 'Away');
      switch (play.event_type) {
        case 'SHOT':
          if (play.event_subtype === 'FG3_MADE') return `${teamLabel} team +3 PTS`;
          if (play.event_subtype === 'FG2_MADE') return `${teamLabel} team +2 PTS`;
          return `${teamLabel} team scored`;
        case 'FT':
          if (play.event_subtype === 'FT_MADE') return `${teamLabel} team +1 FT`;
          return `${teamLabel} team missed FT`;
        case 'FOUL':
          return `${teamLabel} team foul`;
        default:
          return `${teamLabel} team - ${play.event_type}`;
      }
    }

    // Individual player plays
    switch (play.event_type) {
      case 'SHOT':
        if (play.event_subtype === 'FG3_MADE') return `${playerName} made 3PT (+${points})`;
        if (play.event_subtype === 'FG3_MISS') return `${playerName} missed 3PT`;
        if (play.event_subtype === 'FG2_MADE') return `${playerName} made 2PT (+${points})`;
        if (play.event_subtype === 'FG2_MISS') return `${playerName} missed 2PT`;
        return `${playerName} ${play.meta?.made ? 'made' : 'missed'} shot`;
      case 'FT':
        if (play.event_subtype === 'FT_MADE') return `${playerName} made FT (+1)`;
        return `${playerName} missed FT`;
      case 'FOUL':
        return `${playerName} committed foul`;
      case 'REBOUND':
        const rebType = play.event_subtype === 'OREB' ? 'offensive' : 'defensive';
        return `${playerName} ${rebType} rebound`;
      case 'ASSIST':
        return `${playerName} assist`;
      case 'STEAL':
        return `${playerName} steal`;
      case 'BLOCK':
        return `${playerName} block`;
      case 'TURNOVER':
        return `${playerName} turnover`;
      default:
        return `${playerName} - ${play.event_type}`;
    }
  };

  // Get badge color based on event
  const getEventBadgeColor = (play) => {
    if (play.event_type === 'SHOT' && play.meta?.made) {
      return play.event_subtype?.includes('FG3') ? 'bg-purple-500' : 'bg-green-500';
    }
    if (play.event_type === 'FT' && play.event_subtype === 'FT_MADE') {
      return 'bg-blue-500';
    }
    if (play.event_type === 'FOUL') {
      return 'bg-yellow-500';
    }
    if (play.event_type === 'TURNOVER') {
      return 'bg-red-500';
    }
    return 'bg-zinc-600';
  };

  // Format time display
  const formatTime = (play) => {
    if (play.clock?.display) return play.clock.display;
    if (play.clock?.time_ms) {
      const totalSec = Math.floor(play.clock.time_ms / 1000);
      const min = Math.floor(totalSec / 60);
      const sec = totalSec % 60;
      return `${min}:${sec.toString().padStart(2, '0')}`;
    }
    return '--:--';
  };

  if (loading) {
    return (
      <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
        <div className="animate-pulse text-zinc-500 text-center py-8">
          Loading plays...
        </div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden" data-testid="play-by-play-feed">
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
        <h3 className="font-semibold text-white flex items-center gap-2">
          <Activity className="w-4 h-4 text-cyan-400" />
          Play-by-Play
        </h3>
        <Badge variant="outline" className="text-zinc-400 text-xs">
          {plays.length} plays
        </Badge>
      </div>

      {/* Plays List */}
      <ScrollArea className="h-[250px] sm:h-[400px]" ref={scrollRef}>
        {plays.length === 0 ? (
          <div className="p-8 text-center text-zinc-500">
            No plays recorded yet
          </div>
        ) : (
          <div className="divide-y divide-zinc-800/50">
            {plays.map((play, idx) => {
              const isHome = play.team_side === 'home';
              const teamColor = isHome ? (homeTeamColor || '#3B82F6') : (awayTeamColor || '#EF4444');
              
              return (
                <div 
                  key={play.seq || idx}
                  className="px-4 py-3 hover:bg-zinc-800/30 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    {/* Team color indicator */}
                    <div 
                      className="w-1 h-full min-h-[40px] rounded-full flex-shrink-0"
                      style={{ backgroundColor: teamColor }}
                    />
                    
                    {/* Event icon */}
                    <div 
                      className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getEventBadgeColor(play)}`}
                    >
                      {getEventIcon(play.event_type, play.event_subtype)}
                    </div>
                    
                    {/* Event details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-white font-medium text-sm">
                          {getEventDescription(play)}
                        </span>
                        {play.points > 0 && (
                          <Badge className="bg-green-500/20 text-green-400 text-xs px-1.5 py-0">
                            +{play.points}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-zinc-500">
                        <span>Q{play.clock?.period || '?'}</span>
                        <span>•</span>
                        <span>{formatTime(play)}</span>
                        {play.player_number && (
                          <>
                            <span>•</span>
                            <span>#{play.player_number}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

export default PlayByPlayFeed;
